import { AlertCircle, CalendarRange } from "lucide-react";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

import { useDiscountAnalyticsData } from "../../hooks/use-discount-analytics-data";
import { useDiscountCohortMatrices } from "../../hooks/use-discount-cohort-matrices";
import { useReportsV2Store } from "../../store/use-reports-v2-store";
import type {
  DiscountAnalyticsReportShape,
  DiscountCohortMatricesShape,
} from "../../types/discount-analytics-types";
import { LockedChartCard } from "../locked-chart-card";
import { DiscountCampaignDetail } from "./discount-campaign-detail";
import { DiscountCampaignTable } from "./discount-campaign-table";
import { DiscountCohortMatrix } from "./discount-cohort-matrix";
import { CampaignDetailMock, CampaignTableMock, SalonBreakdownMock } from "./discount-mock-content";
import { DiscountSalonBreakdownTable } from "./discount-salon-breakdown-table";
import { DiscountScopeToggle } from "./discount-scope-toggle";
import { DiscountSummary } from "./discount-summary";
import { PremiumFeatureOverlay } from "./premium-feature-overlay";

export function DiscountTab() {
  const { t } = useTranslation("reports");
  const { data, comparison, loading, error } = useDiscountAnalyticsData();
  const { data: cohortData } = useDiscountCohortMatrices();
  const selectedDiscountId = useReportsV2Store((s) => s.selectedDiscountId);
  const setSelectedDiscountId = useReportsV2Store((s) => s.setSelectedDiscountId);
  const discountScope = useReportsV2Store((s) => s.discountScope);
  const currentPeriod = useReportsV2Store((s) => s.currentPeriod);
  const cohorts = cohortData as DiscountCohortMatricesShape | null;

  const detailRef = useRef<HTMLDivElement>(null);
  const shouldScrollToDetailRef = useRef(false);

  const scrollToDetail = useCallback(() => {
    detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleSelectCampaign = useCallback(
    (discountId: string) => {
      if (discountId === selectedDiscountId) {
        scrollToDetail();
        return;
      }
      shouldScrollToDetailRef.current = true;
      setSelectedDiscountId(discountId);
    },
    [selectedDiscountId, setSelectedDiscountId, scrollToDetail]
  );

  // Scroll only after a click renders the detail panel, never on initial mount
  useEffect(() => {
    if (!selectedDiscountId || !shouldScrollToDetailRef.current) {
      return;
    }
    shouldScrollToDetailRef.current = false;
    scrollToDetail();
  }, [selectedDiscountId, scrollToDetail]);

  const report = (data ?? null) as DiscountAnalyticsReportShape | null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    const isForbidden = error.graphQLErrors?.some(
      (gqlError) => gqlError.extensions?.code === "FORBIDDEN"
    );
    if (isForbidden) {
      return <PremiumFeatureOverlay />;
    }
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("error")}</AlertDescription>
      </Alert>
    );
  }

  if (!report) {
    return null;
  }

  const selectedCampaign = report.campaigns.find((c) => c.discountId === selectedDiscountId);
  const showFranchiseNotice = discountScope === "FRANCHISE";
  const isPremium = report.isPremiumEnabled;

  const periodLabel =
    currentPeriod?.startDate && currentPeriod?.endDate
      ? `${DateTime.fromJSDate(currentPeriod.startDate).toLocaleString(DateTime.DATE_MED)} → ${DateTime.fromJSDate(currentPeriod.endDate).toLocaleString(DateTime.DATE_MED)}`
      : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <DiscountScopeToggle />
        {showFranchiseNotice ? (
          <p className="text-xs text-muted-foreground">{t("discounts.scope.timezoneNotice")}</p>
        ) : null}
      </div>

      {periodLabel ? (
        <div className="flex items-center gap-2 rounded-lg border-2 border-rose-200 bg-rose-50 px-3 py-2 shadow-sm">
          <CalendarRange className="h-4 w-4 text-rose-700 shrink-0" />
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 min-w-0 flex-1">
            <span className="text-sm font-semibold text-rose-900">
              {t("discounts.periodFilter.label")}
            </span>
            <Badge
              variant="outline"
              className="bg-white border-rose-300 text-rose-900 font-semibold w-fit"
            >
              {periodLabel}
            </Badge>
          </div>
        </div>
      ) : null}

      {report.campaigns.length === 0 ? (
        <Alert>
          <AlertDescription>{t("discounts.empty.noCampaigns")}</AlertDescription>
        </Alert>
      ) : (
        <>
          <DiscountSummary
            data={report}
            comparison={comparison as DiscountAnalyticsReportShape | null}
            isPremium={isPremium}
          />

          {isPremium ? (
            <DiscountCampaignTable
              campaigns={report.campaigns}
              selectedDiscountId={selectedDiscountId}
              onSelect={handleSelectCampaign}
            />
          ) : (
            <LockedChartCard
              title={t("discounts.table.title")}
              mockContent={<CampaignTableMock />}
              height="auto"
            />
          )}

          {isPremium ? (
            selectedCampaign ? (
              <div ref={detailRef} className="scroll-mt-4">
                <DiscountCampaignDetail
                  key={selectedCampaign.discountId}
                  campaign={selectedCampaign}
                />
              </div>
            ) : null
          ) : (
            <LockedChartCard
              title={t("discounts.detail.trendTitle")}
              mockContent={<CampaignDetailMock />}
              height="auto"
            />
          )}

          {report.scope === "FRANCHISE" &&
            (isPremium ? (
              report.salonBreakdown && report.salonBreakdown.length > 0 ? (
                <DiscountSalonBreakdownTable rows={report.salonBreakdown} />
              ) : null
            ) : (
              <LockedChartCard
                title={t("discounts.salonBreakdown.title")}
                mockContent={<SalonBreakdownMock />}
                height="auto"
              />
            ))}

          {isPremium && cohorts && <DiscountCohortMatrix matrices={cohorts} />}
        </>
      )}
    </div>
  );
}
